import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Check, Broom } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ROOMS = ['Kitchen', 'Living Room', 'Bedroom 1', 'Bedroom 2', 'Bedroom 3', 'Bathroom 1', 'Bathroom 2', 'Exterior', 'Pool Area', 'Other'];

export default function CleaningVerifyPage() {
  const { taskId } = useParams();
  const [task, setTask] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [room, setRoom] = useState('Kitchen');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cleaning/${taskId}/verify`).then(r => {
      setTask(r.data.task);
      setPhotos(r.data.photos);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [taskId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/cleaning/${taskId}/photos`, { photo_url: url, room, notes });
      setPhotos(prev => [...prev, data]);
      setUrl('');
      setNotes('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 2000);
    } catch { alert('Error uploading'); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading...</div>;
  if (!task) return <div className="min-h-screen flex items-center justify-center text-sm text-red-500">Task not found.</div>;

  return (
    <div className="min-h-screen bg-zinc-100 p-4" data-testid="cleaning-verify-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-[#0052FF] text-white p-4 flex items-center gap-3 mb-0">
          <Broom weight="bold" size={20} />
          <div>
            <p className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Cleaning Verification</p>
            <p className="text-xs text-white/70">{task.property_name}</p>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 border-t-0 p-4 mb-4">
          <p className="text-sm text-zinc-700 mb-1"><span className="font-bold">Task:</span> {task.notes}</p>
          <p className="text-xs text-zinc-500">Scheduled: {task.scheduled_date} | Status: {task.status}</p>
        </div>

        {/* Upload form */}
        <div className="bg-white border border-zinc-200 p-4 mb-4">
          <h3 className="text-sm font-bold text-zinc-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Upload Verification Photo</h3>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-zinc-700 mb-1 uppercase tracking-wide">Photo URL</label>
              <input value={url} onChange={e => setUrl(e.target.value)} required className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" placeholder="https://... (paste image URL)" data-testid="photo-url-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1 uppercase tracking-wide">Room</label>
                <select value={room} onChange={e => setRoom(e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="photo-room-select">
                  {ROOMS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 mb-1 uppercase tracking-wide">Notes</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2.5 border border-zinc-200 text-sm rounded-sm" placeholder="Optional" data-testid="photo-notes-input" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="w-full py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm flex items-center justify-center gap-2" data-testid="photo-submit-btn">
              {submitted ? <><Check size={14} /> Uploaded!</> : submitting ? 'Uploading...' : <><Camera size={14} /> Upload Photo</>}
            </button>
          </form>
        </div>

        {/* Uploaded photos */}
        {photos.length > 0 && (
          <div className="bg-white border border-zinc-200 p-4">
            <h3 className="text-sm font-bold text-zinc-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Uploaded Photos ({photos.length})</h3>
            <div className="grid grid-cols-2 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="border border-zinc-200 rounded-sm overflow-hidden" data-testid={`uploaded-photo-${i}`}>
                  {p.photo_url && <img src={p.photo_url} alt={p.room} className="w-full h-24 object-cover" />}
                  <div className="p-2">
                    <p className="text-xs font-bold text-zinc-700">{p.room}</p>
                    {p.notes && <p className="text-xs text-zinc-500">{p.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
